// https://annuities.myaccount.pacificlife.com/enroll-ui/entity-owned
import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import axios from "axios";
import { QuestionCircle } from "react-bootstrap-icons";
import formatTIN from "utils/formatTIN";
import {
    CLIENT,
    REQUIRED_FIELDS,
    MYACCOUNT_LOGIN_PAGE_URL_PATH,
    REGISTRATION_SUCCESSFUL_URL_PATH
} from "./constants";
import {
    emailValidation,
    userNameValidation,
    emailVerifyMatch,
    tinValidation
} from "./validation";
import { analytics } from "./analytics";
import { Header, Footer, Disclaimer } from "./components";

const EntityOwnedReg = () => {
    const [user, setUser] = useState({
        role: "",
        firstName: "",
        lastName: "",
        email: "",
        verifyEmail: "",
        firmId: "",
        crd: "",
        contractNumber: "",
        tin: "",
        dob: "",
        id: "",
        activeContract: null,
        plRelation: null
    });
    const [errors, setErrors] = useState({
        firmId: "",
        crd: "",
        email: "",
        verifyEmail: "",
        id: "",
        tin: "",
        contractNumber: ""
    });
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        // check if all fields are filled out
        const role = CLIENT;
        const requiredFields = REQUIRED_FIELDS[role];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
        }
        // check if there is any error
        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }
        setSubmitDisabled(false);
    }, [user, errors]);

    useEffect(() => {
        document.title = "Entity-Owned Registration | Pacific Life";
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const payload = { [name]: value };
        setUser((prev) => ({ ...prev, ...payload }));
    };

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";

        if (name === "email") {
            const emailValid = emailValidation(value);
            errorMessage = emailValid ? "" : "Enter valid email";
        }

        if (name === "verifyEmail") {
            errorMessage = emailVerifyMatch(user.email, value)
                ? ""
                : "Email addresses do not match";
        }

        if (name === "id") {
            const valid = userNameValidation(value);
            const tooShortOrLong = value.length < 6 || value.length > 8;
            const hasSpecialCharOrWhiteSpace = /\W|\D/g.test(value);
            if (valid) {
                errorMessage = "";
            } else if (tooShortOrLong) {
                errorMessage =
                    "User Name must be between 6 and 8 characters long";
            } else if (hasSpecialCharOrWhiteSpace) {
                errorMessage =
                    "User Name must not contain spaces or special characters";
            }
        }

        if (name === "tin") {
            const valid = tinValidation(value);
            errorMessage = valid
                ? ""
                : "Please enter a valid Taxpayer Identification Number (TIN)";
        }
        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const handleCancel = () => {
        document.location.href = MYACCOUNT_LOGIN_PAGE_URL_PATH;
    };

    const formatPayload = () => {
        return {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            id: user.id,
            tin: user.tin.replace(/-/g, ""),
            tinType: "T",
            userRole: "entity_owned",
            contractNumber: user.contractNumber
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = formatPayload();
        setSubmitting(true);
        try {
            await axios({
                method: "POST",
                url: "/enroll/annuities/contractholder/register/entityowned",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            });
            document.location.href = REGISTRATION_SUCCESSFUL_URL_PATH;
        } catch (error) {
            setToastMessage(
                error.response?.data?.errorMessage ||
                    "Something went wrong. Please try again later."
            );
        }
        setSubmitting(false);
    };

    const closeToast = () => {
        setToastMessage("");
    };

    return (
        <>
            {toastMessage !== "" && (
                <Toast onClose={closeToast} className="toast-cont">
                    <Toast.Header>
                        <strong className="me-auto">{toastMessage}</strong>
                    </Toast.Header>
                </Toast>
            )}
            <Header />
            <div className="mainMyAccount">
                <h1 className="heading">Create an Entity-Owned Account</h1>
                <Form.Label>
                    Register for access to the Client Account website for access
                    to your entity-owned Pacific Life annuity contract.
                    Entity-Owned accounts include plan sponsored, corporate
                    owned and trust accounts.
                </Form.Label>
                <Row>
                    <Col sm={6}>
                        <label className="sub-title">
                            Personal Information
                        </label>
                    </Col>
                </Row>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                name="firstName"
                                autoComplete="off"
                                maxLength={25}
                                value={user.firstName}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                name="lastName"
                                autoComplete="off"
                                maxLength={35}
                                value={user.lastName}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="emailClass">
                                Email
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Enter an email address to be associated with your account"
                                />
                            </Form.Label>
                            <Form.Control
                                name="email"
                                autoComplete="off"
                                maxLength={60}
                                value={user.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.email}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Confirm Email</Form.Label>
                            <Form.Control
                                name="verifyEmail"
                                autoComplete="off"
                                maxLength={60}
                                value={user.verifyEmail}
                                onChange={handleChange}
                                onPaste={(e) => e.preventDefault()}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.verifyEmail}
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6}>
                            <label className="sub-title">
                                Identity Verification
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="tin">
                                Taxpayer Identification Number (TIN)
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Enter a Taxpayer Identification Number (TIN) to verify your identity"
                                />
                            </Form.Label>
                            <Form.Control
                                name="tin"
                                autoComplete="off"
                                placeholder="12-3456789"
                                maxLength={10}
                                value={formatTIN(user.tin)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.tin}
                            </span>
                        </Col>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="contractNumber">
                                Contract/Policy Number
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Your Annuity Contract/Policy Number can be found on your statement"
                                />
                            </Form.Label>
                            <Form.Control
                                placeholder="AB12345678"
                                name="contractNumber"
                                autoComplete="off"
                                maxLength={10}
                                value={user.contractNumber}
                                onChange={handleChange}
                            />
                            {/* this is not working */}
                            <span className="validation-error-message">
                                {errors.contractNumber}
                            </span>
                            {/* this is not working */}
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6}>
                            <label className="sub-title">
                                Login Information
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="createUsername">
                                User Name
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Remember this! You will need it along with your password to access your account information"
                                />
                            </Form.Label>
                            <Form.Control
                                name="id"
                                autoComplete="off"
                                maxLength={8}
                                value={user.id}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.id}
                            </span>
                        </Col>
                        <Col md={6}>
                            <div className="sublabel">
                                Must be between 6 and 8 characters long
                                <br />
                                May contain letters and numbers
                                <br />
                                No spaces or special characters
                            </div>
                        </Col>
                    </Row>
                    <div className="buttons-myaccount">
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                }
                            }}
                            data-track="analytics"
                        >
                            Cancel
                        </Button>{" "}
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={handleSubmit}
                            data-track="analytics"
                            disabled={submitDisabled || submitting}
                        >
                            {submitting ? "Registering" : "Register"}
                        </Button>
                    </div>
                </Form>
                <div id="login" className="link-underline">
                    <a href={MYACCOUNT_LOGIN_PAGE_URL_PATH}>
                        Already have an account?
                    </a>
                </div>
                <Disclaimer />
            </div>
            <Footer />
        </>
    );
};

export default EntityOwnedReg;
